import * as React from 'react';
import {
  Modal,
  HeadingText,
  StackLayout,
  ParagraphText,
  Button,
  Spinner,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';

export interface FullscreenConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  titleId: string;
  descriptionId: string;
  isLoading: boolean;
  loadingTextId: string;
  /** Optional additional content rendered below the loading spinner */
  loadingContent?: React.ReactNode;
  confirmLabel: React.ReactNode;
  onConfirm: () => void;
  isConfirmDisabled?: boolean;
  children: React.ReactNode;
  footerContent?: React.ReactNode;
}

export const FullscreenConfirmationModal = ({
  isOpen,
  onClose,
  titleId,
  descriptionId,
  isLoading,
  loadingTextId,
  loadingContent,
  confirmLabel,
  onConfirm,
  isConfirmDisabled = false,
  children,
  footerContent,
}: FullscreenConfirmationModalProps) => {
  const { formatMessage } = useIntl();

  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Modal.Content
        layout="fullscreen"
        css={{
          '.GDS-modal-content': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        showCloseButton={false}
      >
        <StackLayout
          orientation="vertical"
          spacing="$half"
          horizontalAlignment="stretch"
          css={{ height: '100%' }}
        >
          <StackLayout
            orientation="horizontal"
            horizontalAlignment="end"
            css={{ flexGrow: 0, marginBlockEnd: '-$quarter' }}
          >
            <Modal.Close>
              <Button
                priority="tertiary"
                quiet
                icon="tinyClose"
                hideLabel
                size="small"
                className="GDS-modal-close-button"
              >
                {formatMessage({ id: 'CLOSE' })}
              </Button>
            </Modal.Close>
          </StackLayout>
          <Modal.Title>
            <HeadingText level="1" size="xxl">
              {formatMessage({ id: titleId })}
            </HeadingText>
          </Modal.Title>
          <Modal.Description>
            <ParagraphText>
              {formatMessage({ id: descriptionId })}
            </ParagraphText>
          </Modal.Description>
          {isLoading ? (
            <StackLayout
              orientation="vertical"
              spacing="$one"
              horizontalAlignment="center"
              verticalAlignment="center"
              css={{ minHeight: 400 }}
            >
              <Spinner
                loading
                loadingText={formatMessage({ id: loadingTextId })}
              />
              {loadingContent}
            </StackLayout>
          ) : (
            <>
              <StackLayout
                orientation="vertical"
                spacing="$one"
                horizontalAlignment="stretch"
                css={{
                  padding: '$one',
                  backgroundColor: '$surfaceBackgroundSecondary',
                  borderColor: '$onSurfaceBorderSubdued',
                  borderWidth: '$thin',
                  borderStyle: 'solid',
                  flexGrow: 1,
                  overflow: 'auto',
                }}
              >
                {children}
              </StackLayout>
              <StackLayout
                orientation="horizontal"
                spacing="$one"
                horizontalAlignment="end"
                verticalAlignment="center"
                css={{ flex: '0 0 auto' }}
              >
                {footerContent}
                <Button onClick={onConfirm} disabled={isConfirmDisabled}>
                  {confirmLabel}
                </Button>
              </StackLayout>
            </>
          )}
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
